import { useState, forwardRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import logo from "@/assets/logo.png";

const ContactFooter = forwardRef((_, ref) => {
  const [form, setForm] = useState({
    name: "",
    institution: "",
    email: "",
    phone: "",
    message: "",
    website: "" // 🕳️ Honeypot field
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Por favor, preencha os campos obrigatórios.");
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch('/api/send_contact.php', { // Ajusta o caminho se necessário
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      const data = await res.json();
      
      if (res.ok && data.success) {
        toast.success(data.message);
        setForm({ name: "", institution: "", email: "", phone: "", message: "", website: "" });
      } else {
        toast.error(data.message || "Erro ao enviar o pedido.");
      }
    } catch {
      toast.error("Falha de rede. Verifica a tua ligação.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <section ref={ref} id="contacto" className="py-24 bg-background border-t border-border">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold tracking-tight mb-2">Pronto para inventariar o seu património?</h2>
            <p className="text-muted-foreground">Junte-se às instituições que já confiam no Index Rerum. Agende uma demonstração.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 text-left">
            {/* 🕳️ HONEYPOT INVISÍVEL (NUNCA usar display:none ou hidden attribute) */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={handleChange}
              className="absolute -left-[9999px] opacity-0 w-0 h-0 pointer-events-none"
              aria-hidden="true"
            />

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block font-semibold">Nome *</label>
                <Input name="name" value={form.name} onChange={handleChange} placeholder="O seu nome" maxLength={100} required />
              </div>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block font-semibold">Instituição</label>
                <Input name="institution" value={form.institution} onChange={handleChange} placeholder="Nome da instituição" maxLength={150} />
              </div>
            </div>

            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block font-semibold">Email *</label>
              <Input type="email" name="email" value={form.email} onChange={handleChange} placeholder="email@instituicao.pt" maxLength={255} required />
            </div>

            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block font-semibold">Contacto telefónico *</label>
              <Input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="+351 912 345 678" maxLength={20} required />
            </div>

            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block font-semibold">Mensagem</label>
              <Textarea name="message" value={form.message} onChange={handleChange} placeholder="Descreva brevemente o que procura..." maxLength={1000} className="min-h-[120px]" />
            </div>

            <Button type="submit" variant="hero" size="lg" className="w-full text-base" disabled={isSubmitting}>
              {isSubmitting ? "A enviar..." : "Enviar Pedido de Contacto"}
            </Button>
          </form>
        </div>
      </section>

      <footer className="bg-foreground text-background py-10 md:py-14">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-8">
            <div>
              <img src={logo} alt="Index Rerum by EVOKE IT" className="h-8 mb-3 brightness-0 invert" />
              <p className="text-background/50 text-sm italic leading-relaxed">
                Sistema integrado de inventário e gestão de património 100% web e responsivo.
              </p>
            </div>
            <div>
              <h4 className="font-sans text-xs font-bold text-background/70 uppercase tracking-[0.2em] mb-4">Produto</h4>
              <ul className="space-y-2.5">
                <li><a href="#funcionalidades" className="text-sm text-background/50 hover:text-background transition-colors">Funcionalidades</a></li>
                <li><a href="#para-quem" className="text-sm text-background/50 hover:text-background transition-colors">Para Quem</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-sans text-xs font-bold text-background/70 uppercase tracking-[0.2em] mb-4">Empresa</h4>
              <ul className="space-y-2.5">
                <li><a href="https://evoke.pt/" target="_blank" rel="noopener noreferrer" className="text-sm text-background/50 hover:text-background transition-colors">Sobre Nós</a></li>
                <li><a href="https://www.linkedin.com/company/evoke-it" target="_blank" rel="noopener noreferrer" className="text-sm text-background/50 hover:text-background transition-colors">LinkedIn</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-sans text-xs font-bold text-background/70 uppercase tracking-[0.2em] mb-4">Legal</h4>
              <ul className="space-y-2.5">
                <li><a href="/privacidade" className="text-sm text-background/50 hover:text-background transition-colors">Privacidade</a></li>
                <li><a href="/termos" className="text-sm text-background/50 hover:text-background transition-colors">Termos</a></li>
                <li><a href="/privacidade" className="text-sm text-background/50 hover:text-background transition-colors">Cookies</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-background/10 text-center">
            <p className="text-background/40 text-sm">© 2026 Index Rerum by EVOKE IT. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </>
  );
});

ContactFooter.displayName = "ContactFooter";
export default ContactFooter;